<script>
import { cloneDeep } from 'lodash-es';
import ItineraryPricesDefaultObjects from '../../../variables/itineraryPrices/itineraryPricesDefaultObjects';
import tripDirections from '../../../variables/tripDirections';
import ItineraryPricesPriceIntervalCreator from './ItineraryPricesPriceIntervalCreator';
import ItineraryPricesPriceIntervalTable from './ItineraryPricesPriceIntervalTable';
import ItineraryMatrixAgesModal from '../ItineraryMatrixAgesModal';
import ItineraryMatrixTexts from '../ItineraryMatrixTexts';
import ItineraryPricesContractsModal from './ItineraryPricesContractsModal';

export default {
  name: 'ItineraryPricesPriceIntervalSettings',
  components: {
    ItineraryPricesPriceIntervalCreator,
    ItineraryPricesPriceIntervalTable,
    ItineraryMatrixAgesModal,
    ItineraryMatrixTexts,
    ItineraryPricesContractsModal,
  },
  props: {
    selectedPriceIntervalIndex: {
      type: Number,
      default: 0,
    },
    priceIntervals: {
      type: Array,
      default: () => [],
    },
    itineraryLength: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      days: [
        { id: 0, day: 'sunday', label: this.$t('nonDynamicTransport.days.sunday') },
        { id: 1, day: 'monday', label: this.$t('nonDynamicTransport.days.monday') },
        { id: 2, day: 'tuesday', label: this.$t('nonDynamicTransport.days.tuesday') },
        { id: 3, day: 'wednesday', label: this.$t('nonDynamicTransport.days.wednesday') },
        { id: 4, day: 'thursday', label: this.$t('nonDynamicTransport.days.thursday') },
        { id: 5, day: 'friday', label: this.$t('nonDynamicTransport.days.friday') },
        { id: 6, day: 'saturday', label: this.$t('nonDynamicTransport.days.saturday') },
      ],
      localPriceIntervals: [],
      editDialog: false,
      editedPriceInterval: cloneDeep(ItineraryPricesDefaultObjects.DEFAULT_PRICE_INTERVAL),
      editIndex: null,
      intervalsValidated: true,
    };
  },
  computed: {
    priceIntervalValidationMessage() {
      return `NOTICE: The itinerary is ${this.itineraryLength} days,
      some of the intervals might not be long enough to cover this`;
    },
  },
  watch: {
    priceIntervals: {
      immediate: true,
      handler(value) {
        this.localPriceIntervals = cloneDeep(value);
      },
      deep: true,
    },
  },
  methods: {
    deletePriceInterval(interval) {
      const priceIntervals = cloneDeep(this.localPriceIntervals);
      const foundIntervalIndex = priceIntervals.findIndex(priceInterval => priceInterval.id === interval.id);

      priceIntervals.splice(foundIntervalIndex, 1);
      this.localPriceIntervals = [ ...priceIntervals ];
      this.$emit('update', this.localPriceIntervals);
    },
    editPriceInterval(payload) {
      const { index, item } = payload;
      this.editIndex = index;
      this.editedPriceInterval = item;
      this.setDays(item.intervalDays);
      this.editDialog = true;
    },
    savePriceInterval(priceInterval) {
      if (this.editIndex === null) {
        this.localPriceIntervals.push(priceInterval);
      } else {
        this.localPriceIntervals[this.editIndex] = cloneDeep(priceInterval);
        this.editIndex = null;
      }
      this.validatePriceIntervalArrivalLength();
      this.$emit('update', this.localPriceIntervals);
      this.editedPriceInterval = cloneDeep(ItineraryPricesDefaultObjects.DEFAULT_PRICE_INTERVAL);
      this.editDialog = false;
    },
    validatePriceIntervalArrivalLength() {
      for (let i = 0; i < this.localPriceIntervals.length; i++) {
        const intervalLength = this.getDaysBetweenDates(
          this.localPriceIntervals[i].arrivalFrom,
          this.localPriceIntervals[i].arrivalTo,
        );
        if (intervalLength < this.itineraryLength) {
          this.intervalsValidated = false;
          let combinedIntervalLength = intervalLength;
          let nextIndex = i + 1;
          let currentIndex = i;
          // Keep checking next until interval is long enough and its the next day
          for (let x = nextIndex; x < this.localPriceIntervals.length; x++) {
            // Checking if next day, else it wont cover the period.
            if (this.isNextDay(
              this.localPriceIntervals[currentIndex].arrivalTo,
              this.localPriceIntervals[nextIndex].arrivalFrom
            )) {
              const nextIntervalLength = this.getDaysBetweenDates(
                this.localPriceIntervals[x].arrivalFrom,
                this.localPriceIntervals[x].arrivalTo,
              );
              combinedIntervalLength += nextIntervalLength;
              if (combinedIntervalLength >= this.itineraryLength) {
                this.intervalsValidated = true;
                i = nextIndex + 1;
                break;
              } else {
                this.intervalsValidated = false;
                currentIndex += 1;
                nextIndex += 1;
              }
            } else {
              i = x + 1;
              this.intervalsValidated = false;
            }
          }
        } else {
          this.intervalsValidated = true;
        }
      }
    },
    getDaysBetweenDates(firstDate, secondDate) {
      const start = this.$moment(firstDate, 'YYYY-MM-DD');
      const end = this.$moment(secondDate, 'YYYY-MM-DD');
      // Plus 1 to include the first day.
      return this.$moment.duration(end.diff(start)).asDays() + 1;
    },
    isNextDay(currentDay, nextDay) {
      const localCurrentDay = this.$moment(currentDay, 'YYYY-MM-DD');
      const localNextDay = this.$moment(nextDay, 'YYYY-MM-DD');
      return localNextDay.isSame(localCurrentDay.add(1, 'days'));
    },
    updateActiveStatus(priceIntervalId) {
      const priceIntervalIndex = this.localPriceIntervals.findIndex(
        priceInterval => priceInterval.id === priceIntervalId,
      );
      this.localPriceIntervals[priceIntervalIndex].active = !this.localPriceIntervals[priceIntervalIndex].active;
      this.$emit('change-active-status', priceIntervalIndex);
    },
    setDays(interval) {
      const outboundDays = interval.filter(day => day.direction === tripDirections.OUTBOUND);
      const filteredOutboundDays = [];

      outboundDays.forEach(outboundDay => {
        this.days.filter(day => {
          if (outboundDay.day === day.id) {
            filteredOutboundDays.push(day);
          }
          return null;
        });
      });

      this.outboundDays = [ ...filteredOutboundDays ];
    },
  },
};
</script>

<template>
  <div>
    <v-toolbar flat color="white">
      <h1>Price intervals</h1>
      <v-layout>
        <v-flex
          v-if="!intervalsValidated"
          ml-5
          mt-3
          row
        >
          <v-icon color="warning">error</v-icon> {{ priceIntervalValidationMessage }}
        </v-flex>
      </v-layout>
      <v-spacer/>
      <itinerary-prices-contracts-modal />
      <itinerary-matrix-texts />
      <itinerary-matrix-ages-modal />
      <itinerary-prices-price-interval-creator
        :edit-dialog="editDialog"
        :price-intervals="localPriceIntervals"
        :price-interval="editedPriceInterval"
        :itinerary-length="itineraryLength"
        :edit-index="editIndex"
        @save="savePriceInterval"
        @close="editDialog = !editDialog"
      />
    </v-toolbar>
    <itinerary-prices-price-interval-table
      :price-intervals="localPriceIntervals"
      @edit="editPriceInterval"
      @delete="deletePriceInterval"
      @update-active-status="updateActiveStatus"
    />
  </div>
</template>

<style scoped lang="scss">
  @import '../../../assets/scss/variables';

  .selected-price-interval {
    background-color: $highlight-color-lightgrey;
  }

  .active {
    color: $success-color;
  }

  .deactivated {
    color: $warning-color;
  }
</style>



// WEBPACK FOOTER //
// src/components/itinerary/itineraryPrices/ItineraryPricesPriceIntervalSettings.vue